// Экран 2 из ТЗ раздел 3.3: оформление подписки. Единственный подключённый способ оплаты —
// Telegram Payments (см. app/api/routes/miniapp/checkout.py — там же объяснено, почему ЮKassa и
// ввод промокода на этот экран сознательно не добавлены). Тариф для оплаты берём из уже
// загруженного списка тарифов (fetchPlans) по planId из URL — отдельного GET /miniapp/plans/:id
// backend не заводил, список тарифов и так маленький и уже гоняется на экране 1.
//
// Количество устройств (1..plan.device_limit, скидка за опт — см. src/lib/pricing.ts) выбирается
// здесь, не на витрине тарифов — там показывается только цена "от" за 1 устройство (уточнение
// пользователя 2026-08-20, см. docs/DECISIONS.md).
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError, checkout, fetchPlans, type Plan } from "../api/client";
import { openInvoice } from "../api/openInvoice";
import { computeQuantityPrice } from "../lib/pricing";

export function CheckoutScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { planId } = useParams<{ planId: string }>();
  // undefined — ещё грузится, null — тариф не найден/неактивен.
  const [plan, setPlan] = useState<Plan | null | undefined>(undefined);
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchPlans()
      .then((plans) => {
        if (cancelled) return;
        setPlan(plans.find((p) => p.id === Number(planId)) ?? null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [planId]);

  const handlePay = () => {
    if (!plan) return;
    setError(null);
    setPaying(true);
    checkout(plan.id, quantity)
      .then(({ invoice_link }) => {
        openInvoice(invoice_link, (status) => {
          setPaying(false);
          if (status === "paid") {
            navigate("/subscription");
          } else if (status !== "cancelled") {
            setError(t("checkout_payment_failed"));
          }
        });
      })
      .catch((err: unknown) => {
        setPaying(false);
        setError(err instanceof ApiError ? err.message : String(err));
      });
  };

  const amountDue = plan ? computeQuantityPrice(plan.price, quantity) : 0;

  return (
    <div>
      <h1>{t("checkout_title")}</h1>
      {error && <p role="alert">{error}</p>}
      {plan === undefined && !error && <p>{t("loading")}</p>}
      {plan === null && <p>{t("checkout_plan_not_found")}</p>}
      {plan && (
        <div>
          <p>
            <strong>{plan.title}</strong> — {plan.price} {plan.currency}/{plan.duration_days}
            {t("days_short")}
          </p>

          <p>{t("checkout_quantity_label")}</p>
          <select
            value={quantity}
            onChange={(event) => setQuantity(Number(event.target.value))}
          >
            {Array.from({ length: plan.device_limit }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>

          <p>
            {t("checkout_amount_due")}: <strong>{amountDue} {plan.currency}/{plan.duration_days}{t("days_short")}</strong>
          </p>

          <p>{t("checkout_payment_method")}</p>
          <button type="button" onClick={handlePay} disabled={paying}>
            {paying ? t("loading") : t("checkout_pay")}
          </button>
        </div>
      )}
    </div>
  );
}
